/**
 * Entity Admin React Query hooks
 *
 * Wraps the cloud-owned REST endpoints that replaced the legacy
 * `entityAdminEntities`, `entityAdminContracts`, `entityAdminContractParts`,
 * `entityAdminContractDevices`, and `entityAdminUsers` JSONata read pipelines.
 *
 * Endpoints (all under `/api/core/entityAdmin/`, mounted ahead of the
 * integration-common proxy):
 *   getEntities, getContracts, getContractParts, getContractDevices, getUsers
 *
 * Phase 3 of the configurator-v1 platform-admin migration.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import {
  useSelectedEntityId,
  useAuthtoken,
} from 'stores/useAuthStore';
import { callCoreApi } from 'utils/request';

export const ENTITY_ADMIN_ENTITIES_QUERY_KEY = ['entityAdmin', 'entities'] as const;
export const ENTITY_ADMIN_CONTRACTS_QUERY_KEY = ['entityAdmin', 'contracts'] as const;
export const ENTITY_ADMIN_CONTRACT_PARTS_QUERY_KEY = [
  'entityAdmin',
  'contract-parts',
] as const;
export const ENTITY_ADMIN_CONTRACT_DEVICES_QUERY_KEY = [
  'entityAdmin',
  'contract-devices',
] as const;
export const ENTITY_ADMIN_USERS_QUERY_KEY = ['entityAdmin', 'users'] as const;

function useAuthToken() {
  return useAuthtoken() || '';
}

interface QueryOptions {
  enabled?: boolean;
  refetchOnMount?: boolean | 'always';
  staleTime?: number;
}

// -----------------------------------------------------------------------------
// Entities
// -----------------------------------------------------------------------------

/**
 * Calling shapes mirror the JSONata pipelines:
 *   - no params         → list of entity rows
 *   - { entityId }      → single entity row (legacy pipelines returned the
 *                         row inside an array; the cloud REST endpoint
 *                         normalizes to either depending on filter mode)
 */
export function useEntityAdminEntities<T = any>(
  params: { entityId?: number | string } = {},
  options: QueryOptions = {},
) {
  const authToken = useAuthToken();
  const enabled = options.enabled !== false;
  return useQuery<T, Error>({
    queryKey: [...ENTITY_ADMIN_ENTITIES_QUERY_KEY, params],
    queryFn: async () => {
      const result = await callCoreApi(
        'entityAdmin/getEntities',
        params,
        authToken,
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load entities');
      }
      return result.data as T;
    },
    enabled,
  });
}

// -----------------------------------------------------------------------------
// Contracts
// -----------------------------------------------------------------------------

export function useEntityAdminContracts<T = any>(
  params: { entityId?: number; contractId?: number } = {},
  options: QueryOptions = {},
) {
  const authToken = useAuthToken();
  const enabled = options.enabled !== false;
  return useQuery<T, Error>({
    queryKey: [...ENTITY_ADMIN_CONTRACTS_QUERY_KEY, params],
    queryFn: async () => {
      const result = await callCoreApi(
        'entityAdmin/getContracts',
        params,
        authToken,
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load contracts');
      }
      return result.data as T;
    },
    enabled,
  });
}

// -----------------------------------------------------------------------------
// Contract parts
// -----------------------------------------------------------------------------

export function useEntityAdminContractParts<T = any>(
  contractId: number | undefined,
  options: QueryOptions = {},
) {
  const authToken = useAuthToken();
  const enabled = options.enabled !== false && contractId != null;
  return useQuery<T, Error>({
    queryKey: [...ENTITY_ADMIN_CONTRACT_PARTS_QUERY_KEY, contractId ?? null],
    queryFn: async () => {
      const result = await callCoreApi(
        'entityAdmin/getContractParts',
        { contractId },
        authToken,
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load contract parts');
      }
      return result.data as T;
    },
    enabled,
  });
}

// -----------------------------------------------------------------------------
// Contract devices
// -----------------------------------------------------------------------------

export function useEntityAdminContractDevices<T = any>(
  params: { entityId?: number; contractDeviceId?: number } = {},
  options: QueryOptions = {},
) {
  const authToken = useAuthToken();
  const enabled = options.enabled !== false;
  const { refetchOnMount, staleTime } = options;
  return useQuery<T, Error>({
    queryKey: [...ENTITY_ADMIN_CONTRACT_DEVICES_QUERY_KEY, params],
    queryFn: async () => {
      // Entity-scoped read: devicesAdmin/getContractDevices self-asserts on
      // the caller's entity (globalAdmin bypass), so entity admins can list
      // their own pool. The staff-only entityAdmin/getContractDevices requires
      // globalAdmin and 403s for normal entity admins.
      const result = await callCoreApi(
        'devicesAdmin/getContractDevices',
        params,
        authToken,
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load contract devices');
      }
      return result.data as T;
    },
    enabled,
    ...(refetchOnMount !== undefined ? { refetchOnMount } : {}),
    ...(staleTime !== undefined ? { staleTime } : {}),
  });
}

// -----------------------------------------------------------------------------
// Users
// -----------------------------------------------------------------------------

export function useEntityAdminUsers<T = any>(
  params: { entityId?: number; userName?: string } = {},
  options: QueryOptions = {},
) {
  const authToken = useAuthToken();
  const enabled = options.enabled !== false;
  return useQuery<T, Error>({
    queryKey: [...ENTITY_ADMIN_USERS_QUERY_KEY, params],
    queryFn: async () => {
      const result = await callCoreApi(
        'entityAdmin/getUsers',
        params,
        authToken,
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load users');
      }
      return result.data as T;
    },
    enabled,
  });
}

// -----------------------------------------------------------------------------
// Cross-cutting invalidation helper
// -----------------------------------------------------------------------------

export function useInvalidateEntityAdminReads() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ENTITY_ADMIN_ENTITIES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ENTITY_ADMIN_CONTRACTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ENTITY_ADMIN_CONTRACT_PARTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ENTITY_ADMIN_CONTRACT_DEVICES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ENTITY_ADMIN_USERS_QUERY_KEY });
  }, [queryClient]);
}

/**
 * Invalidate the entity-admin reads whenever the active entity changes.
 *
 * The entity-admin hooks key their cache on the call params only
 * (`{ contractDeviceId }`, `{ entityId }`, …), not on the active entity, so a
 * global admin switching entities in-app would otherwise be served the
 * previous entity's cached rows until a hard refresh (the cause of the
 * device-setup wizard seeding stale/empty name + resource after a switch).
 *
 * Mount once near the app root.
 */
export function useEntityAdminEntitySwitchSync(): void {
  const invalidate = useInvalidateEntityAdminReads();
  const selectedEntityId = useSelectedEntityId();
  const previousEntityId = useRef(selectedEntityId);
  useEffect(() => {
    if (previousEntityId.current !== selectedEntityId) {
      previousEntityId.current = selectedEntityId;
      invalidate();
    }
  }, [selectedEntityId, invalidate]);
}
